import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Dialog } from '@mui/material'
import { toast } from 'react-toastify'
import Checkbox from 'components/atoms/Checkbox'
import documentAccessAPI, { announceChange } from 'features/documentAccess/documentAccessAPI'
import { SKIP_REASON, errorMessage } from 'features/documentAccess/accessCopy'
import { ACCENT, BORDER, INK, LINK, MUTED, PrimaryButton, SecondaryButton } from 'features/actions/workspace/ui'

const MAX = 50

const RequestAccessDialog = ({ open, bpId, files = [], onClose, onDone, zIndex = 1500 }) => {
  const [selected, setSelected] = useState(() => new Set())
  const [message, setMessage] = useState('')
  const [q, setQ] = useState('')
  const [sending, setSending] = useState(false)

  const wasOpen = useRef(false)
  const filesRef = useRef(files)
  filesRef.current = files

  useEffect(() => {
    if (open && !wasOpen.current) {
      setSelected(new Set(filesRef.current.slice(0, MAX).map(f => f.id)))
      setMessage('')
      setQ('')
    }
    wasOpen.current = open
  }, [open])

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase()
    return term ? files.filter(f => (f.name || '').toLowerCase().includes(term)) : files
  }, [files, q])

  const toggle = id => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else if (next.size < MAX) next.add(id)
    return next
  })

  const submit = async () => {
    if (!selected.size) return
    setSending(true)
    try {
      const res = await documentAccessAPI.request(bpId, [...selected], message.trim())
      const results = res?.data?.data || []
      const created = results.filter(r => r.status === 'created').length
      const skipped = results.filter(r => r.status === 'skipped')
      if (created) toast.success(created === 1 ? 'Request sent. We will email you when it is decided.' : `${created} requests sent. We will email you as each one is decided.`)
      if (skipped.length) {
        const reasons = [...new Set(skipped.map(s => SKIP_REASON[s.reason] || 'it could not be requested'))]
        toast.info(`${skipped.length} not sent because ${reasons.join(', or ')}.`)
      }
      announceChange({ requested: results.filter(r => r.status === 'created').map(r => ({ fileId: r.fileId, requestId: r.requestId })) })
      onDone?.(results)
      onClose()
    } catch (err) {
      toast.error(err?.response?.status === 403
        ? 'Only members of this Building Passport can ask to open its documents. Ask its owner to add you first.'
        : errorMessage(err))
    } finally {
      setSending(false)
    }
  }

  const single = files.length === 1

  return (
    <Dialog open={!!open} onClose={sending ? undefined : onClose} fullWidth maxWidth='sm' aria-labelledby='request-access-title' sx={{ zIndex }}
      PaperProps={{ sx: { borderRadius: '12px', m: 2, width: 'calc(100% - 32px)', maxHeight: 'calc(100% - 32px)' } }}>
      <Body>
        <Title id='request-access-title'>{single ? 'Ask to open this document' : 'Ask to open these documents'}</Title>
        <Help>
          We will ask the people who decide for {single ? 'this document' : 'each document'}: usually the organisation's Super Admins or a nominated approver, or the person who uploaded it.
          You will get an email when a decision is made. Each document is approved or refused on its own.
        </Help>

        {single ? (
          <SingleName>
            <RowText>
              <RowName>{files[0]?.name}</RowName>
              {describe(files[0]) && <RowMeta>{describe(files[0])}</RowMeta>}
            </RowText>
          </SingleName>
        ) : (
          <>
            <Toolbar>
              <Search type='search' value={q} onChange={e => setQ(e.target.value)} placeholder='Search documents' aria-label='Search documents' />
              <Count>{selected.size} of {files.length} selected{files.length > MAX ? ` (up to ${MAX} at a time)` : ''}</Count>
            </Toolbar>
            <List>
              {visible.map(f => (
                <Row key={f.id} onClick={() => toggle(f.id)}>
                  <CheckSlot onClick={e => e.stopPropagation()}>
                    <Checkbox name={f.id} isChecked={selected.has(f.id)} onChange={() => toggle(f.id)} />
                  </CheckSlot>
                  <RowText>
                    <RowName>{f.name}</RowName>
                    {describe(f) && <RowMeta>{describe(f)}</RowMeta>}
                  </RowText>
                </Row>
              ))}
              {!visible.length && <Empty>No documents match that search.</Empty>}
            </List>
          </>
        )}

        <Label htmlFor='request-access-message'>Why do you need {single ? 'it' : 'them'}? (optional)</Label>
        <Message
          id='request-access-message'
          value={message}
          maxLength={500}
          onChange={e => setMessage(e.target.value)}
          placeholder='For example: we are the new managing agent and need the fire risk assessment.'
        />

        <Footer>
          <SecondaryButton type='button' onClick={onClose} disabled={sending}>Cancel</SecondaryButton>
          <PrimaryButton type='button' onClick={submit} disabled={sending || !selected.size}>
            {sending ? 'Sending…' : single ? 'Send request' : `Send ${selected.size} request${selected.size === 1 ? '' : 's'}`}
          </PrimaryButton>
        </Footer>
      </Body>
    </Dialog>
  )
}

export default RequestAccessDialog

const describe = (f) => [
  f?.location === 'unit' ? 'On a unit' : f?.location === 'task' ? 'Attached to a task' : null,
  f?.uploaderName && `Uploaded by ${f.uploaderName}`,
].filter(Boolean).join(' · ')

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px;
  overflow: hidden;
  @media (max-width: 768px) { padding: 16px; }
`

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${INK};
`

const Help = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${MUTED};
`

const SingleName = styled.div`
  padding: 12px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  overflow-wrap: anywhere;
`

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
`

const Search = styled.input`
  flex: 1 1 220px;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  &:focus { outline: 2px solid ${ACCENT}; outline-offset: 1px; }
`

const Count = styled.span`
  font-size: 14px;
  color: ${MUTED};
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-height: 120px;
  max-height: 320px;
  overflow-y: auto;
`

const Row = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 6px 10px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  cursor: pointer;
  &:hover { border-color: ${ACCENT}; }
`

const CheckSlot = styled.span`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  height: 20px;
  label { display: block; margin: 0; padding-left: 20px; height: 16px; line-height: 16px; }
`

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const RowName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  overflow-wrap: anywhere;
`

const RowMeta = styled.span`
  font-size: 13px;
  color: ${MUTED};
`

const Empty = styled.div`
  padding: 8px 2px;
  font-size: 15px;
  color: ${MUTED};
`

const Label = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`

const Message = styled.textarea`
  min-height: 80px;
  padding: 10px 12px;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;
  resize: vertical;
  &:focus { outline: 2px solid ${ACCENT}; outline-offset: 1px; }
  &:focus-visible { outline-color: ${LINK}; }
`

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`
